import { MatchProfile } from "../types/matching";
import { supabase } from "./supabase";
import { MAX_SELF_CHAT_ROOMS } from "../config/publicConfig";

export interface IMatchingService {
  getRecommendedMatches(): Promise<MatchProfile[]>;
  getMatchDetail(id: string): Promise<MatchProfile | null>;
  handoverToChild(slotId: string): Promise<{ inviteLink?: string; shareCode?: string; childAssigned: boolean }>;
  claimHandover(params: { slotId?: string; parentId?: string; shareCode?: string }): Promise<void>;
  generateShareCode(matchId: string): Promise<{ shareCode: string; match_id: string } | null>;
  redeemShareCode(code: string): Promise<{ success: boolean; message: string; creator_id?: string }>;
  getAcceptedMatchCount(): Promise<{ count: number; maxAllowed: number }>;
}

export const matchingService: IMatchingService = {
  // Get recommended matches via Edge Function
  getRecommendedMatches: async (): Promise<MatchProfile[]> => {
    const { data, error } = await supabase.functions.invoke("match-slots", {
      body: { action: "get_matches" },
    });

    if (error) {
      console.error("Error fetching matches:", error);
      return [];
    }

    // Map backend response to frontend requirements
    return (data?.matches || []).map((profile: any) => ({
      id: profile.id,
      name: profile.username || "未知用户",
      avatarUrl: profile.avatar_url,
      age: profile.age,
      location: profile.location || "未知",
      bio: profile.bio || "",
      matchScore: profile.match_score || 0,
      isOnline: false,
    }));
  },

  getMatchDetail: async (id: string): Promise<MatchProfile | null> => {
    // For detail, we might just query the DB directly or use the same EF
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", id)
      .single();

    if (error) return null;

    return {
      id: data.id,
      name: data.username,
      avatarUrl: data.avatar_url,
      age: data.age,
      location: data.location,
      bio: data.bio,
      matchScore: 0, // Detail view might not need score
      isOnline: false,
    };
  },

  // Parent hands over slot to child
  handoverToChild: async (slotId: string): Promise<{ inviteLink?: string; shareCode?: string; childAssigned: boolean }> => {
    const { data, error } = await supabase.functions.invoke("match-slots", {
      body: {
        action: "handover",
        slot_id: slotId
      },
    });

    if (error) throw error;
    return data;
  },

  // Child claims the handed over slot
  claimHandover: async (params: { slotId?: string; parentId?: string; shareCode?: string }): Promise<void> => {
    const { error } = await supabase.functions.invoke("match-slots", {
      body: {
        action: "claim_handover",
        slot_id: params.slotId,
        parent_id: params.parentId,
        share_code: params.shareCode
      },
    });

    if (error) throw error;
  },

  // 生成分享码（针对指定 match_id 聊天对象）
  generateShareCode: async (matchId: string): Promise<{ shareCode: string; match_id: string } | null> => {
    const { data, error } = await supabase.functions.invoke("match-slots", {
      body: { action: "generate_share_code", match_id: matchId },
    });

    if (error) throw error;
    return data;
  },

  // 兑换分享码（被分享者调用，建立聊天）
  redeemShareCode: async (code: string): Promise<{ success: boolean; message: string; creator_id?: string }> => {
    const { data, error } = await supabase.functions.invoke("match-slots", {
      body: {
        action: "redeem_share_code",
        share_code: code,
      },
    });

    if (error) {
      return { success: false, message: error.message || "兑换失败" };
    }
    return data;
  },

  // 获取 accepted 匹配数量
  getAcceptedMatchCount: async (): Promise<{ count: number; maxAllowed: number }> => {
    const { data, error } = await supabase.functions.invoke("match-slots", {
      body: { action: "get_accepted_match_count" },
    });

    if (error) return { count: 0, maxAllowed: MAX_SELF_CHAT_ROOMS };
    return data;
  },
};
